import { useEffect, useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { uploadFile, attachmentUrl } from "@/lib/upload";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function EditProfile({ open, onOpenChange }) {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName(user?.name || "");
      setAvatar(user?.avatar || "");
    }
  }, [open, user]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    setUploading(true);
    try {
      const att = await uploadFile(file);
      setAvatar(attachmentUrl(att));
    } catch (err) {
      toast.error("Could not upload the picture");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/auth/profile", { name, avatar });
      updateUser(r.data.user);
      toast.success("Profile updated");
      onOpenChange(false);
    } catch (err) {
      toast.error("Could not save your profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-[#16181F] border-[#2E3242] text-gray-200" data-testid="edit-profile-dialog">
        <DialogTitle className="font-display font-bold text-lg">Edit profile</DialogTitle>
        <DialogDescription className="text-gray-500 text-sm">Update your name and profile picture.</DialogDescription>

        <div className="flex flex-col items-center pt-2">
          <button
            type="button"
            data-testid="profile-avatar-picker"
            onClick={() => fileRef.current?.click()}
            className="relative group"
          >
            <img src={avatar} alt="" className="w-24 h-24 rounded-full object-cover border-2 border-red-900/50" />
            <span className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
              {uploading ? <Loader2 className="w-6 h-6 text-white animate-spin" /> : <Camera className="w-6 h-6 text-white" />}
            </span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPick} data-testid="profile-avatar-input" />
          <p className="text-[11px] text-gray-500 mt-2">Tap the photo to change it</p>
        </div>

        <div className="mt-4">
          <Label className="text-xs text-gray-400">Display name</Label>
          <Input
            data-testid="profile-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 bg-[#0F1015] border-[#2E3242] focus-visible:ring-red-600 h-10"
          />
        </div>

        <div className="mt-5 flex items-center gap-2">
          <Button
            data-testid="profile-save-button"
            onClick={save}
            disabled={saving || uploading || !name.trim()}
            className="ruby-gradient text-white border border-red-900/40 h-10 flex-1"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
          </Button>
          <button
            onClick={() => onOpenChange(false)}
            className="h-10 px-3 text-sm text-gray-400 hover:text-gray-200"
          >
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
